import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Calendar, Users, Clock, MapPin, Award, ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { hackathonAPI, eventsAPI } from '../../services/api';
import api from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Timeline from '../../components/ui/Timeline';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Hackathon } from '../../types/events';
import { useAuth } from '../../contexts/AuthContext';
import { useRegistration } from '../../contexts/RegistrationContext';
import UnregisterDialog from '../../components/ui/UnregisterDialog';

const HackathonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const { 
    checkRegistration, 
    getRegistrationStatus, 
    isChecking,
    unregisterFromHackathon,
    clearRegistrationStatus
  } = useRegistration();
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [associatedEvent, setAssociatedEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUnregisterDialog, setShowUnregisterDialog] = useState(false);
  const [unregisterLoading, setUnregisterLoading] = useState(false);
  const [showAvailableAspirants, setShowAvailableAspirants] = useState(false);
  const [availableAspirants, setAvailableAspirants] = useState<any[]>([]);
  const [aspirantsLoading, setAspirantsLoading] = useState(false);
  const [aspirantsSearchTerm, setAspirantsSearchTerm] = useState('');

  // Don't render buttons until user context is loaded
  const isUserLoaded = !authLoading && user !== null && user !== undefined;
  
  // Debug user object
  console.log('👤 User context:', { 
    authLoading, 
    isUserLoaded, 
    user: user ? { 
      id: user._id, 
      email: user.email, 
      role: user.role 
    } : null 
  });
  
  // Show loading spinner if auth is still loading
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  useEffect(() => {
    if (id) {
      fetchHackathon();
    }
  }, [id]);

  useEffect(() => {
    console.log('🔄 useEffect triggered:', { id, isUserLoaded, user: !!user, userEmail: user?.email });
    if (id && isUserLoaded && user && !isChecking(id!)) {
      console.log('✅ Calling checkRegistration');
      checkRegistration(id!, 'hackathon');
    } else {
      console.log('❌ Not calling checkRegistration:', { 
        hasId: !!id, 
        isUserLoaded, 
        hasUser: !!user,
        isChecking: isChecking(id!)
      });
    }
  }, [id, isUserLoaded, user?._id]); // Only depend on stable values

  const fetchHackathon = async () => {
    try {
      setLoading(true);
      const response = await hackathonAPI.getHackathonById(id!);
      setHackathon(response);
      
      // Find the associated event for this hackathon
      try {
        const eventsResponse = await eventsAPI.getAllEvents();
        const hackathonEvent = eventsResponse.find((event: any) => 
          event.hackathon === response._id && event.eventType === 'hackathon'
        );
        setAssociatedEvent(hackathonEvent);
        console.log('🔍 Found associated event for hackathon:', {
          hackathonId: response._id,
          eventId: hackathonEvent?._id,
          eventTitle: hackathonEvent?.title,
          registrationUrl: hackathonEvent ? `/events/${hackathonEvent._id}/register` : 'NO EVENT FOUND'
        });
      } catch (eventErr) {
        console.error('Error fetching associated event:', eventErr);
        setAssociatedEvent(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch hackathon details');
      console.error('Error fetching hackathon:', err);
    } finally {
      setLoading(false);
    }
  };



  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Registration Closed';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days} days left`;
    if (hours > 0) return `${hours} hours left`;
    return `${minutes} minutes left`;
  };

  const getAspirantExperience = (aspirant: any) => {
    try {
      // Handle experience array (new format)
      if (aspirant.experience && Array.isArray(aspirant.experience) && aspirant.experience.length > 0) {
        const totalYears = aspirant.experience.reduce((total: number, exp: any) => {
          const startDate = new Date(exp.startDate);
          const endDate = exp.endDate ? new Date(exp.endDate) : new Date();
          const years = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
          return total + years;
        }, 0);
        return `${Math.round(totalYears)} years`;
      }
      
      // Handle professionalInfo (legacy format)
      if (aspirant.professionalInfo?.yearsOfExperience) {
        return `${aspirant.professionalInfo.yearsOfExperience} years`;
      }
      
      // Handle education-based calculation
      if (aspirant.education?.graduationYear) {
        const currentYear = new Date().getFullYear();
        const yearsSinceGraduation = currentYear - aspirant.education.graduationYear;
        if (yearsSinceGraduation >= 0) {
          return yearsSinceGraduation === 0 ? 'Student' : `${yearsSinceGraduation} years`;
        }
      }
      
      return 'Student';
    } catch (error) {
      console.error('Error calculating experience for aspirant:', aspirant, error);
      return 'Student';
    }
  };

  const refreshRegistrationStatus = () => {
    if (id && user) {
      clearRegistrationStatus(id);
      checkRegistration(id, 'hackathon');
    }
  };

  const handleUnregister = async (leaveTeam: boolean) => {
    if (!id) return;
    
    try {
      setUnregisterLoading(true);
      await unregisterFromHackathon(id, leaveTeam);
      setShowUnregisterDialog(false);
      // The registration status will be automatically cleared by the context
    } catch (error) {
      console.error('Failed to unregister:', error);
    } finally {
      setUnregisterLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const response = await fetch(`/api/payments/receipt/${paymentId}`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt-${paymentId}.html`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Failed to download receipt');
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
    }
  };

  const fetchAvailableAspirants = async () => {
    try {
      setAspirantsLoading(true);
      const response = await fetch(`/api/hackathons/${id}/available-aspirants`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvailableAspirants(data.aspirants || []);
      } else {
        console.error('Failed to fetch available aspirants');
      }
    } catch (error) {
      console.error('Error fetching available aspirants:', error);
    } finally {
      setAspirantsLoading(false);
    }
  };

  const filteredAspirants = availableAspirants.filter(aspirant => {
    const searchTerm = aspirantsSearchTerm.toLowerCase();
    return (
      aspirant.firstName?.toLowerCase().includes(searchTerm) ||
      aspirant.lastName?.toLowerCase().includes(searchTerm) ||
      aspirant.email?.toLowerCase().includes(searchTerm) ||
      aspirant.phone?.toLowerCase().includes(searchTerm) ||
      aspirant.address?.toLowerCase().includes(searchTerm) ||
      aspirant.primarySkills?.some((skill: string) => skill.toLowerCase().includes(searchTerm)) ||
      aspirant.secondarySkills?.some((skill: string) => skill.toLowerCase().includes(searchTerm))
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !hackathon) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600">{error || 'Hackathon not found'}</div>
      </div>
    );
  }

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{hackathon.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Event Details</h3>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span>Start: {formatDate(hackathon.startDate)} at {formatTime(hackathon.startDate)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span>End: {formatDate(hackathon.endDate)} at {formatTime(hackathon.endDate)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  <span>Registration Deadline: {formatDate(hackathon.registrationDeadline)} at {formatTime(hackathon.registrationDeadline)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <Users className="h-4 w-4 mr-2 text-gray-500" />
                  <span>Team Size: {hackathon.minTeamSize}-{hackathon.maxTeamSize} members</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Participation</h3>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <Users className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{hackathon.currentTeams}/{hackathon.maxTeams} teams registered</span>
                </div>
                <div className="flex items-center text-sm">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{getTimeRemaining(hackathon.registrationDeadline)}</span>
                </div>
                <div className="flex items-center text-sm">
                  <StatusBadge status={hackathon.status} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'timeline',
      label: 'Timeline',
      content: (
        <div>
                     {hackathon.phases && hackathon.phases.length > 0 ? (
             <Timeline phases={hackathon.phases as any} />
           ) : (
            <div className="text-center py-12">
              <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No phases defined</h3>
              <p className="text-gray-600">Timeline will be available once phases are added</p>
            </div>
          )}
        </div>
      )
    },
    {
      id: 'problems',
      label: 'Problem Statements',
      content: (
        <div className="space-y-4">
          {hackathon.problemStatements.map((problem, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{problem.title}</CardTitle>
                  <StatusBadge status={problem.difficulty} />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-2">{problem.description}</p>
                <div className="text-sm text-gray-500">Category: {problem.category}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )
    },
    {
      id: 'prizes',
      label: 'Prizes',
      content: (
        <div className="space-y-4">
          {hackathon.prizes.map((prize, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-center mb-2">
                  <Award className="h-5 w-5 mr-2 text-yellow-500" />
                  <CardTitle>{prize.description}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-bold text-blue-600">{prize.value}</p>
                <p className="text-sm text-gray-500 mt-1">Rank: {prize.rank}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )
    },
    {
      id: 'rules',
      label: 'Rules & Requirements',
      content: (
        <div className="space-y-6">
          {hackathon.rules && hackathon.rules.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Rules</h3>
              <ul className="list-disc list-inside space-y-2">
                {hackathon.rules.map((rule, index) => (
                  <li key={index} className="text-gray-700">{rule}</li>
                ))}
              </ul>
            </div>
          )}
          
          {hackathon.requirements && hackathon.requirements.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-3">Requirements</h3>
              <ul className="list-disc list-inside space-y-2">
                {hackathon.requirements.map((requirement, index) => (
                  <li key={index} className="text-gray-700">{requirement}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/events" className="flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Events
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{hackathon.title}</h1>
            <div className="flex items-center space-x-4">
              <StatusBadge status={hackathon.status} />
              <span className="text-gray-600">
                {hackathon.currentTeams}/{hackathon.maxTeams} teams
              </span>
            </div>
          </div>
          
          <div className="flex space-x-3">

            
            {isUserLoaded && user?.role === 'admin' && (
              <>
                <Link
                  to={`/admin/hackathons/${hackathon._id}/edit`}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-2 inline" />
                  Edit
                </Link>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => {
                    // TODO: Implement hackathon delete functionality
                    console.log('Delete hackathon functionality not yet implemented');
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            {isUserLoaded && user?.role === 'aspirant' && (
              <>
                {(() => {
                  const registrationStatus = id ? getRegistrationStatus(id) : null;
                  const isRegistered = registrationStatus?.isRegistered || false;
                  
                  console.log('🎯 Button rendering state:', {
                    isChecking: isChecking(id!),
                    isRegistered,
                    userRole: user?.role,
                    isUserLoaded,
                    registrationStatus
                  });
                  
                  if (isChecking(id!)) {
                    // Show loading while checking registration
                    return (
                      <div className="bg-gray-200 text-gray-600 px-6 py-2 rounded-md">
                        Checking...
                      </div>
                    );
                  } else if (!isRegistered) {
                    // User not registered - show register button
                    if (!associatedEvent) {
                      // If no associated event, allow direct hackathon registration
                      return (
                        <Link
                          to={`/events/${hackathon._id}/register`}
                          className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                          Register Now
                        </Link>
                      );
                    }
                    
                    return (
                      <Link
                        to={`/events/${associatedEvent._id}/register`}
                        className="bg-primary hover:bg-primary-dark text-white font-medium px-6 py-2 transition-all duration-300 shadow-lg hover:shadow-xl"
                      >
                        Register Now
                      </Link>
                    );
                  } else {
                    // User registered - show team management buttons
                    const hasTeam = registrationStatus?.registration?.hasTeam || false;
                    
                    return (
                      <>
                        {!hasTeam && (
                          <>
                            <Link
                              to={`/hackathons/${hackathon._id}/teams/discover`}
                              className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px] text-center"
                            >
                              Browse Teams
                            </Link>
                            <Link
                              to={`/teams/create?hackathon=${hackathon._id}`}
                              className="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px] text-center"
                            >
                              Create Team
                            </Link>
                            <Button
                              onClick={() => {
                                setShowAvailableAspirants(true);
                                fetchAvailableAspirants();
                              }}
                              className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px]"
                            >
                              Available Aspirants
                            </Button>
                          </>
                        )}
                        {hasTeam && (
                          <>
                            <Link
                              to={`/hackathons/${hackathon._id}/teams/discover`}
                              className="bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px] text-center"
                            >
                              Browse Teams
                            </Link>
                            <Link
                              to="/teams/my-teams"
                              className="bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px] text-center"
                            >
                              View My Team
                            </Link>
                          </>
                        )}
                        {registrationStatus?.registration?.paymentId && (
                          <button
                            onClick={() => handleDownloadReceipt(registrationStatus.registration.paymentId)}
                            className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition-colors flex items-center text-sm font-medium whitespace-nowrap min-w-[120px]"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download Receipt
                          </button>
                        )}
                        {hasTeam && (
                          <Link
                            to="/teams/my-teams"
                            className="bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors text-sm font-medium whitespace-nowrap min-w-[120px] text-center"
                          >
                            View My Team
                          </Link>
                        )}
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700 border-red-600 hover:border-red-700 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap min-w-[120px]"
                          onClick={() => setShowUnregisterDialog(true)}
                        >
                          Unregister
                        </Button>
                      </>
                    );
                  }
                })()}
              </>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex w-full overflow-x-auto mb-6">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id} className="flex-shrink-0">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>

      {/* Unregister Dialog */}
      <UnregisterDialog
        isOpen={showUnregisterDialog}
        onClose={() => setShowUnregisterDialog(false)}
        onConfirm={handleUnregister}
        eventTitle={hackathon?.title || ''}
        eventType="hackathon"
        eventFee={250} // Fixed fee for hackathons
        isPaidEvent={true} // Hackathons are always paid
        isInTeam={false} // TODO: Check if user is in team
        isTeamLeader={false} // TODO: Check if user is team leader
        loading={unregisterLoading}
      />

      {/* Available Aspirants Dialog */}
      <Dialog open={showAvailableAspirants} onOpenChange={setShowAvailableAspirants}>
        <DialogContent className="max-w-6xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Available Aspirants for {hackathon?.title}</DialogTitle>
            <DialogDescription>
              View all registered and confirmed aspirants who are not part of any team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex flex-col h-full">
            {/* Search Box */}
            <div className="flex items-center space-x-2">
              <Input
                placeholder="Search by name, email, phone, location, or skills..."
                value={aspirantsSearchTerm}
                onChange={(e) => setAspirantsSearchTerm(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={fetchAvailableAspirants}
                disabled={aspirantsLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {aspirantsLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
              </Button>
            </div>

            {/* Count Display */}
            <div className="text-sm text-gray-600">
              Showing {filteredAspirants.length} of {availableAspirants.length} aspirants
              {aspirantsSearchTerm && (
                <span className="ml-2 text-blue-600">
                  (filtered by "{aspirantsSearchTerm}")
                </span>
              )}
            </div>

            {/* Aspirants Table */}
            <div className="border rounded-lg overflow-hidden flex-1">
              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Skills</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Experience</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {aspirantsLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <LoadingSpinner size="lg" />
                        </td>
                      </tr>
                    ) : filteredAspirants.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          {aspirantsSearchTerm ? 'No aspirants found matching your search' : 'No available aspirants found'}
                        </td>
                      </tr>
                    ) : (
                      filteredAspirants.map((aspirant, index) => (
                        <tr key={aspirant._id || index} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">
                              {aspirant.firstName} {aspirant.lastName}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {aspirant.email}
                              </div>
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {aspirant.phone}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-sm text-gray-600">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              {aspirant.address || 'Not specified'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              {aspirant.primarySkills && aspirant.primarySkills.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {aspirant.primarySkills.map((skill: string, skillIndex: number) => (
                                    <span key={skillIndex} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                              {aspirant.secondarySkills && aspirant.secondarySkills.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  {aspirant.secondarySkills.map((skill: string, skillIndex: number) => (
                                    <span key={skillIndex} className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center text-sm text-gray-600">
                              {String(getAspirantExperience(aspirant)).toLowerCase() === 'student' ? (
                                <span className="mr-2 font-medium text-blue-600">Student</span>
                              ) : (
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                                </svg>
                              )}
                              {getAspirantExperience(aspirant)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvailableAspirants(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HackathonDetail; 